import { NAV_HIDDEN_PAGE_IDS } from "../features";
import { parseISODateLocal } from "../lib/dates";
import { rememberStoreFromSearch, searchCarryingStore } from "../lib/selectedStore";
import { canAccessHrTools, grantCovers, isEmployeeRole, normalizeSessionRole } from "../identity/access";

export const APP_PAGE_IDS = new Set([
  "announcements",
  "schedule",
  "builder",
  "leave",
  "eod-stock",
  "academy",
  "admin",
  "portioning",
  "bonus",
  "procureiq",
  "procure-orders",
  "procure-order",
  "recipes",
  "hr-dashboard",
  "settings",
  "identity",
]);

const AUTH_PATH_SLUGS = new Set(["login", "register", "forgot", "forgot-password", "reset", "reset-password"]);

export function defaultPageForRole(role) {
  const r = normalizeSessionRole(role);
  if (r === "hr") return "hr-dashboard";
  if (r === "employee") return "schedule";
  if (r === "store_manager" || r === "admin" || r === "brand_manager" || r === "ops_manager")
    return "builder";
  return "announcements";
}

export function defaultPageForUser(user) {
  if (canAccessHrTools(user) && normalizeSessionRole(user?.role) === "hr") {
    return "hr-dashboard";
  }
  if (canAccessRecipes(user) && isEmployeeRole(user) && !grantCovers(user?.grants, "ops", "schedule", "view")) {
    return "recipes";
  }
  if (canAccessRecipes(user) && isEmployeeRole(user) && grantCovers(user?.grants, "ops", "recipes", "edit")) {
    const hasScheduleView = grantCovers(user?.grants, "ops", "schedule", "view");
    if (!hasScheduleView) return "recipes";
  }
  return defaultPageForRole(user?.role);
}

export function normalizePathname(pathname) {
  const raw = String(pathname || "/").split("?")[0].split("#")[0];
  return raw.replace(/\/+$/, "") || "/";
}

export function pathSegments(pathname) {
  const parts = normalizePathname(pathname).split("/").filter(Boolean);
  if (parts[0] === "auth") return parts.slice(1);
  return parts;
}

export function pageFromPathname(pathname) {
  const slug = pathSegments(pathname)[0] || "";
  if (!slug || AUTH_PATH_SLUGS.has(slug)) return null;
  if (slug === "schedule-builder") return "builder";
  return APP_PAGE_IDS.has(slug) ? slug : null;
}

export function authModeFromPathname(pathname) {
  const slug = pathSegments(pathname)[0] || "";
  if (slug === "register") return "register";
  if (slug === "forgot" || slug === "forgot-password") return "forgot";
  if (slug === "reset" || slug === "reset-password") return "reset";
  return "login";
}

export function pathForAppPage(page) {
  return `/${page || "announcements"}`;
}

/** Full href for sidebar/nav links (path + store-carrying search when needed). */
export function hrefForAppPage(page, search) {
  const path = pathForAppPage(page);
  if (!pageKeepsSearch(page)) return path;
  const q =
    typeof window !== "undefined"
      ? searchCarryingStore(page, search != null ? search : window.location.search)
      : searchCarryingStore(page, search || "");
  return `${path}${q}`;
}

/** Plain left-click → SPA navigate; Cmd/Ctrl/Shift/middle-click → browser default (new tab). */
export function shouldHandleAsSpaNav(event) {
  if (!event || event.defaultPrevented) return false;
  if (event.button != null && event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  return true;
}

const BUILDER_VIEWS = new Set(["schedule", "optimal", "timeline"]);

export function builderQueryFromSearch(search) {
  const { locationId, storeName, week } = storeParamFromSearch(search);
  const raw = String(search || "");
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  const viewRaw = String(params.get("view") || "").trim().toLowerCase();
  const view = BUILDER_VIEWS.has(viewRaw) ? viewRaw : "schedule";
  return { store: storeName, locationId, week, view };
}

export function searchForBuilder({ locationId, week, view } = {}) {
  const params = new URLSearchParams();
  if (locationId != null && locationId !== "") params.set("store", String(locationId));
  const weekStart = String(week || "").trim();
  if (weekStart) params.set("week", weekStart);
  if (view === "optimal" || view === "timeline") params.set("view", view);
  const q = params.toString();
  return q ? `?${q}` : "";
}

export function pageKeepsSearch(page) {
  return (
    page === "builder" ||
    page === "leave" ||
    page === "eod-stock" ||
    page === "admin" ||
    page === "hr-dashboard" ||
    page === "procureiq" ||
    page === "procure-orders" ||
    page === "procure-order" ||
    page === "announcements"
  );
}

export function storeParamFromSearch(search) {
  const raw = String(search || "");
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  const store = String(params.get("store") || "").trim();
  const week = String(params.get("week") || "").trim();
  if (/^\d+$/.test(store)) return { locationId: Number(store), storeName: "", week };
  return { locationId: null, storeName: store, week };
}

const PROCUREIQ_TABS = new Set([
  "consumption",
  "today",
  "week",
  "history",
  "suppliers",
  "forecast-menu",
  "forecast-leaves",
  "forecast-contrib",
  "order-build",
  "order-analysis",
]);

export function procureOrdersQueryFromSearch(search) {
  const { locationId } = storeParamFromSearch(search);
  const raw = String(search || "");
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  const brandRaw = String(params.get("brand") || "").trim();
  const brandId = /^\d+$/.test(brandRaw) ? Number(brandRaw) : null;
  const orderKey = String(params.get("order") || "").trim() || null;
  return { locationId, brandId, orderKey };
}

export function searchForProcureOrders({ locationId, brandId, orderKey } = {}) {
  const params = new URLSearchParams();
  if (locationId != null && locationId !== "") params.set("store", String(locationId));
  if (brandId != null && brandId !== "") params.set("brand", String(brandId));
  const key = String(orderKey || "").trim();
  if (key) params.set("order", key);
  const q = params.toString();
  return q ? `?${q}` : "";
}

/** Absolute Ops URL for a receipt-review order (new tab; bare page, not Supy). */
export function opsProcureOrderHref({ locationId, brandId, orderKey } = {}) {
  const key = String(orderKey || "").trim();
  if (!key) return null;
  const search = searchForProcureOrders({ locationId, brandId, orderKey: key });
  if (typeof window === "undefined" || !window.location?.origin) {
    return `/procure-order${search}`;
  }
  return `${window.location.origin}/procure-order${search}`;
}

/** Monday ISO YYYY-MM-DD, or null if missing/invalid. */
export function procureiqWeekStartFromSearch(search) {
  const raw = String(search || "");
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  const week = String(params.get("week_start") || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(week)) return null;
  const [y, m, d] = week.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime()) || dt.getDay() !== 1) return null;
  return week;
}

/** Calendar day YYYY-MM-DD for consumption tab, or null. */
export function procureiqDateFromSearch(search) {
  const raw = String(search || "");
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  const day = String(params.get("date") || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const [y, m, d] = day.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return null;
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return day;
}

const PROCUREIQ_CONSUMPTION_VIEWS = new Set(["materials", "sold"]);

/** Consumption sub-view: materials (default) | sold. */
export function procureiqConsumptionViewFromSearch(search) {
  const raw = String(search || "");
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  const view = String(params.get("view") || "").trim().toLowerCase();
  return PROCUREIQ_CONSUMPTION_VIEWS.has(view) ? view : "materials";
}

export function procureiqTabFromSearch(search) {
  const raw = String(search || "");
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  const tab = String(params.get("tab") || "").trim().toLowerCase();
  return PROCUREIQ_TABS.has(tab) ? tab : "consumption";
}

export function procureiqQueryFromSearch(search) {
  const raw = String(search || "");
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  const { locationId } = storeParamFromSearch(search);
  const brandRaw = String(params.get("brand") || "").trim();
  const brandId = /^\d+$/.test(brandRaw) ? Number(brandRaw) : null;
  return {
    tab: procureiqTabFromSearch(search),
    locationId,
    brandId,
    weekStart: procureiqWeekStartFromSearch(search),
    date: procureiqDateFromSearch(search),
    view: procureiqConsumptionViewFromSearch(search),
  };
}

export function searchForProcureiq({ tab, locationId, brandId, weekStart, date, view } = {}) {
  const params = new URLSearchParams();
  params.set("tab", PROCUREIQ_TABS.has(tab) ? tab : "consumption");
  if (locationId != null && locationId !== "") params.set("store", String(locationId));
  if (brandId != null && brandId !== "") params.set("brand", String(brandId));
  if (tab === "consumption" && date && /^\d{4}-\d{2}-\d{2}$/.test(String(date))) {
    params.set("date", String(date));
    if (PROCUREIQ_CONSUMPTION_VIEWS.has(view) && view !== "materials") {
      params.set("view", view);
    }
  } else if (weekStart && /^\d{4}-\d{2}-\d{2}$/.test(String(weekStart))) {
    params.set("week_start", String(weekStart));
  }
  return `?${params.toString()}`;
}

const ADMIN_TABS = new Set(["employees", "platform-users", "leave", "requests", "stores", "headcount"]);

export function adminQueryFromSearch(search) {
  const raw = String(search || "");
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  const parsed = storeParamFromSearch(search);
  const tabRaw = String(params.get("tab") || "").trim().toLowerCase();
  const allStores = String(params.get("store") || "").trim().toLowerCase() === "all";
  return {
    ...parsed,
    allStores,
    tab: ADMIN_TABS.has(tabRaw) ? tabRaw : "employees",
  };
}

export function searchForAdmin({ locationId, allStores, tab } = {}) {
  const params = new URLSearchParams();
  if (allStores) params.set("store", "all");
  else if (locationId != null && locationId !== "") params.set("store", String(locationId));
  if (ADMIN_TABS.has(tab)) params.set("tab", tab);
  const q = params.toString();
  return q ? `?${q}` : "";
}

export function searchForStorePage({ locationId, week } = {}) {
  const params = new URLSearchParams();
  if (locationId != null && locationId !== "") params.set("store", String(locationId));
  const weekStart = String(week || "").trim();
  if (weekStart) params.set("week", weekStart);
  const q = params.toString();
  return q ? `?${q}` : "";
}

const HR_TABS = new Set(["changes", "foxline", "ergani", "schedule"]);

export function hrTabFromSearch(search) {
  const raw = String(search || "");
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  const tab = String(params.get("tab") || "").trim().toLowerCase();
  return HR_TABS.has(tab) ? tab : "";
}

export function searchForHrDashboard({ locationId, week, tab } = {}) {
  const params = new URLSearchParams(searchForStorePage({ locationId, week }).replace(/^\?/, ""));
  if (HR_TABS.has(tab)) params.set("tab", tab);
  const q = params.toString();
  return q ? `?${q}` : "";
}

export function leaveQueryFromSearch(search) {
  const { locationId, storeName } = storeParamFromSearch(search);
  return { store: storeName, locationId };
}

export function searchForLeave({ locationId } = {}) {
  return searchForStorePage({ locationId });
}

export function pathForAuthMode(mode) {
  if (mode === "register") return "/register";
  if (mode === "forgot") return "/forgot";
  if (mode === "reset") return "/reset";
  return "/";
}

function pinFavicon() {
  document.head.querySelectorAll('link[rel="icon"]').forEach((link) => {
    link.parentNode?.appendChild(link);
  });
}

export function replaceBrowserPath(path, search) {
  rememberHistoryEntry(path, search, { push: false });
}

export function rememberHistoryEntry(path, search, { push = false } = {}) {
  rememberStoreFromSearch(search);
  try {
    const next = `${path}${search || ""}`;
    const current = `${window.location.pathname}${window.location.search}`;
    const title = historyTitle(path, search);
    if (push && current !== next) window.history.pushState({}, "", next);
    else window.history.replaceState({}, "", next);
    document.title = title;
    pinFavicon();
  } catch {
    /* ignore */
  }
}

export function landingPageForUser(user) {
  const fromUrl = pageFromPathname(window.location.pathname);
  if (fromUrl && !NAV_HIDDEN_PAGE_IDS.has(fromUrl)) return fromUrl;
  return defaultPageForUser(user);
}

export function canAccessRecipes(user) {
  if (!user) return false;
  if (grantCovers(user.grants, "ops", "recipes", "view")) return true;
  const role = normalizeSessionRole(user.role);
  return (role === "admin" && user?.store === "Network") || String(user.role || "").toLowerCase() === "chef";
}

const EOD_STOCK_STORE_TOKENS = Object.freeze([
  "PG", "DiNAS", "Heiden", "Skoufa", "Omirou", "Kolokotroni", "Tsamadou", "Dousmani", "Panormou",
]);

export function isEodStockPhysicalStore(storeName) {
  const s = String(storeName || "").trim();
  if (!s || s === "All Stores" || s === "Network") return false;
  if (/^\s*hub\b/i.test(s)) return false;
  return EOD_STOCK_STORE_TOKENS.some((tok) => s.includes(tok));
}

export function canAccessEodStock(user) {
  if (!user) return false;
  if (grantCovers(user.grants, "ops", "eod_stock", "view")) return true;
  const role = normalizeSessionRole(user.role);
  if (role === "admin" || role === "brand_manager") return true;
  if (String(user.role || "").toLowerCase() === "chef") return true;
  if (role !== "store_manager") return false;
  return isEodStockPhysicalStore(user.store);
}

export function formatEodStockHeaderDate(iso, lang) {
  const d = parseISODateLocal(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const locale = lang === "gr" ? "el-GR" : "en-GB";
  return d.toLocaleDateString(locale, {
    weekday: "long", day: "numeric", month: "short", year: "numeric",
  });
}

export function formatEodStockTime(isoOrDate, lang) {
  if (!isoOrDate) return "";
  const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
  if (Number.isNaN(d.getTime())) return "";
  const locale = lang === "gr" ? "el-GR" : "en-GB";
  return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: false });
}

export const PAGE_TITLES = {
  announcements: { en: "Announcements", gr: "Ανακοινώσεις" },
  schedule: { en: "My Schedule", gr: "Πρόγραμμά μου" },
  builder: { en: "Schedule Builder", gr: "Δημιουργία Προγράμματος" },
  leave: { en: "Leave & Requests", gr: "Άδειες & Αιτήματα" },
  "eod-stock": { en: "End of Day Stock", gr: "Απόθεμα ημέρας" },
  academy: { en: "Academy", gr: "Ακαδημία" },
  admin: { en: "People & Stores", gr: "Άνθρωποι & Καταστήματα" },
  identity: { en: "Identity", gr: "Ταυτότητα" },
  portioning: { en: "Portioning Guide", gr: "Οδηγός Portioning" },
  bonus: { en: "Bonus Calculator", gr: "Υπολογιστής Bonus" },
  procureiq: { en: "ProcureIQ", gr: "ProcureIQ" },
  "procure-orders": { en: "Procure Orders", gr: "Παραγγελίες προμηθειών" },
  "procure-order": { en: "Order", gr: "Παραγγελία" },
  recipes: { en: "Recipes", gr: "Συνταγές" },
  "hr-dashboard": { en: "HR Dashboard", gr: "Πίνακας HR" },
  settings: { en: "Settings", gr: "Ρυθμίσεις" },
};

const HISTORY_TAB_LABELS = {
  today: "Today",
  week: "This week",
  history: "Order history",
  suppliers: "Suppliers",
  "forecast-menu": "Forecast menu",
  "forecast-leaves": "Raw materials",
  "order-build": "Order build",
  changes: "Changes",
  foxline: "SoftOne",
  ergani: "Ergani",
  schedule: "Schedule",
  optimal: "Optimal",
  timeline: "Timeline",
  employees: "Employees",
  "platform-users": "Platform users",
  leave: "Leave",
  requests: "Requests",
  stores: "Stores",
  headcount: "Headcount",
};

export function historyTitle(path, search) {
  const page = pageFromPathname(path) || "";
  const pageLabel = PAGE_TITLES[page]?.en || "STIQ Operations";
  const raw = String(search || "");
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  const parts = [pageLabel];
  const tab = String(params.get("tab") || params.get("view") || "").trim().toLowerCase();
  if (tab && HISTORY_TAB_LABELS[tab]) parts.push(HISTORY_TAB_LABELS[tab]);
  const store = String(params.get("store") || "").trim();
  if (store && store.toLowerCase() !== "all") parts.push(store);
  const week = String(params.get("week") || "").trim();
  if (week) parts.push(week);
  return parts.join(" · ");
}
